import { HttpClient } from '@angular/common/http';
import { Component, HostListener, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Namespace } from 'enums-lib';
import { IHamburgerMenuItem } from 'hamburger-lib';
import { jwtDecode } from 'jwt-decode';
import { MessageNotificationService } from 'message-notification-service-lib';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Identity, IdentityStorage } from 'storage-service-lib';
import { environment } from '../../../environments/environment';

export interface GenerateLoginOtpResponse {
  otp: string;
}

@Component({
  selector: 'app-hamburger-wrapper',
  template: `
    <lib-hamburger
      [menuItems]="menuItems"
      menuTitle="Kyloe AwesomeDocs"
      homeRoute=""
      homeTooltip="Kyloe AwesomeDocs Home"
      [(isOpen)]="isMenuOpen">
    </lib-hamburger>
  `,
  standalone: false
})
export class HamburgerWrapperComponent implements OnDestroy {
  readonly Namespace = Namespace;

  isMenuOpen = false;
  identity: Identity;
  menuItems: IHamburgerMenuItem[] = [];

  private identitySubscription: Subscription;

  constructor(
    private router: Router,
    private messageNotificationService: MessageNotificationService,
    private identityStorage: IdentityStorage,
    private http: HttpClient
  ) {
    this.identitySubscription = identityStorage.observe().subscribe((identity) => {
      this.identity = identity;
      this.updateMenuItems();
    });
    this.identity = identityStorage.get();
    this.updateMenuItems();
  }

  @HostListener('document:keyup', ['$event'])
  protected handleKeyupEvent(event: KeyboardEvent) {
    if (event.ctrlKey && event.altKey && event.code === 'KeyW' && this.hasIdentity) {
      event.preventDefault();
      console.log('Generating and copying login OTP');
      this.generateAndCopyLoginOtp();
    }
  }

  get hasIdentity(): boolean {
    return this.identity !== null;
  }

  private updateMenuItems() {
    this.menuItems = [
      {
        label: 'AwesomeDocs Manager',
        icon: {name: 'extension'},
        isCurrentSection: true
      }
    ];
    if (this.hasIdentity) {
      this.menuItems.push(
        {
          label: 'AwesomeDocs',
          icon: {name: 'extension'},
          action: () => this.goToAwesomeDocs(),
          tooltip: 'Open AwesomeDocs in a new tab',
          showNewTabIcon: true
        },
        {
          label: `Open ${this.identity.namespace.charAt(0).toUpperCase() + this.identity.namespace.slice(1)}`,
          icon: {name: 'dashboard'},
          action: () => this.goToTenantWebsite(),
          tooltip: `Open ${this.identity.namespace.charAt(0).toUpperCase() + this.identity.namespace.slice(1)} in a new tab`,
          showNewTabIcon: true,
          customClass: 'external-link'
        }
      );

      if (this.identity.namespace === Namespace.invenias) {
        this.menuItems.push({
          label: 'Install Chrome Extension',
          imageIcon: 'assets/images/chrome-icon.png',
          action: () => this.goToChromeExtension(),
          tooltip: 'Open the Chrome Store in a new tab',
          showNewTabIcon: true,
          customClass: 'chrome-extension-link external-link'
        });
      }

      this.menuItems.push(
        {
          label: 'Generate login code',
          icon: {name: 'extension'},
          action: () => this.generateAndCopyLoginOtp(),
          tooltip: 'Generate login code (Ctrl+Alt+W)',
          customClass: 'external-link'
        },
        {
          label: 'Switch account',
          iconAfter: {name: 'logout'},
          action: () => this.logOut(),
          tooltip: 'Sign in as another user',
          customClass: 'log-out'
        }
      );
    }
  }

  private goToTenantWebsite() {
    const url = this.getTenantWebsiteUrl(this.identity);
    window.open(url, '_blank');
  }

  goToChromeExtension() {
    window.open(environment.chromeStoreUrl, '_blank');
  }

  private goToAwesomeDocs() {
    window.open(`${environment.kpWebApp}/${this.identity.namespace}/${this.identity.tenant}`, '_blank');
  }

  private generateAndCopyLoginOtp(): void {
      this.generateLoginOtp().pipe(
        tap((res: GenerateLoginOtpResponse) => {
          if (!res?.otp) {
            throw new Error('Login code was not generated');
          }
          navigator.clipboard.writeText(res.otp);
          this.messageNotificationService.success('Code copied to clipboard.');
          this.isMenuOpen = false;
        }),
        catchError(() => {
          this.messageNotificationService.error('Failed to generate login code.');
          return EMPTY;
        })
      )
      .subscribe();
  }

  private generateLoginOtp(): Observable<GenerateLoginOtpResponse> {
    return this.http.post<GenerateLoginOtpResponse>(`${environment.kpHost}/adv2/config/auth/otp/generate`, {});
  }

  private logOut(): void {
    this.identityStorage.clear();
    this.router.navigate(['signin']);
  }

  private getTenantWebsiteUrl(identity: Identity): string {
    if (identity.namespace === Namespace.invenias) {
      return `https://${identity.tenant}.${identity.namespace}.com`;
    }
    if (identity.namespace === Namespace.bullhorn) {
      return 'https://universal.bullhornstaffing.com/universal-login/login';
    }
    if (identity.namespace === Namespace.salesforce) {
      try {
        const decoded = jwtDecode<any>(identity.token);
        const instanceUrl = decoded?.instanceUrl;
        if (instanceUrl) {
          return instanceUrl;
        }
      } catch (e) {
        console.error('Failed to decode Salesforce token', e);
      }
      // Fallback to generic login if token missing/malformed
      return 'https://login.salesforce.com';
    }
    throw new Error(`Unexpected namespace: ${identity.namespace}`);
  }

  ngOnDestroy(): void {
    this.identitySubscription.unsubscribe();
  }
}
